import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Mail, MapPin } from 'lucide-react';
import Button from 'components/crm/ui/Button';
import Chip from 'components/crm/ui/Chip';
import CrmScope from 'components/crm/ui/CrmScope';
import Drawer from 'components/crm/ui/Drawer';
import RichSelect from 'components/crm/ui/RichSelect';
import TextField from 'components/crm/ui/TextField';
import { useToast } from 'components/crm/ui/Toast';
import { isMail } from 'components/mail/scheduleSlots';
import CompanyDirectorySearch from 'components/prospection/CompanyDirectorySearch';
import FollowedCompany, {
  CompanyCard,
} from 'components/prospection/FollowedCompany';
import PhoneField from 'components/prospection/PhoneField';
import ProspectContacts from 'components/prospection/ProspectContacts';
import {
  buildProspectPayload,
  emptyProspectForm,
  isProspectFormValid,
} from 'components/prospection/prospectForm';
import { PROSPECT_SOURCES } from 'helper/prospectOptions';
import { COMMERCIAL_ROLE } from 'helper/roles';
import {
  isCanceledRequest,
  prospectionErrorText,
} from 'helper/prospectionErrors';
import UseContext from 'hooks/useContext';
import useCrmContext from 'hooks/useCrmContext';
import useText from 'hooks/useText';
import ApiDatabase from 'server';
import crmApi from 'server/crm/crmApi';
import { createProspect, listTeam } from 'server/crm/prospectionApi';

// Mounted only while open, so that every opening starts from an empty form.
// `frame` ({ body, footer }) lays it out elsewhere than in its Drawer (the
// slide-over « Ajouter une entreprise », components/companies/AddCompanyPanel).
export const AddProspectForm = ({ onClose, onAdded, frame }) => {
  const { text: prospectionText } = useText('prospection');
  const { text } = useText('prospection.addProspect');
  const { userData } = UseContext();
  const toast = useToast();
  const { call } = useCrmContext();
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  // A commercial takes the prospect by default; the agency manager or a
  // recruiter, who do not prospect, must name the commercial in charge (api).
  const commercial = Number(userData?.role) === COMMERCIAL_ROLE;
  const [form, setForm] = useState(() => ({
    ...emptyProspectForm(),
    commercialId: commercial ? (userData?._id ?? '') : '',
  }));
  const [commercials, setCommercials] = useState([]);
  useEffect(() => {
    listTeam().then(
      (team) => setCommercials((team ?? []).filter((person) => Number(person.role) === COMMERCIAL_ROLE)),
      () => setCommercials([]),
    );
  }, []);
  // Optional field: without the list, the category stays empty and the
  // prospect can still be added.
  const [categories, setCategories] = useState([]);
  useEffect(() => {
    let mounted = true;
    ApiDatabase.getPennyLaneCategories(
      {},
      (data) => mounted && setCategories(data?.items ?? []),
      (error) => {
        if (error?.name !== 'CanceledError') console.error(error);
      },
    );
    return () => {
      mounted = false;
    };
  }, []);
  const [nameLeft, setNameLeft] = useState(false);
  const [busy, setBusy] = useState(false);

  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const pick = (picked) => {
    setCompany(picked);
    setNameLeft(false);
    setForm((f) => ({
      ...f,
      name: picked.name ?? '',
      address: picked.manual ? '' : (picked.address ?? ''),
    }));
  };

  const change = () => setCompany(null);

  const valid = isProspectFormValid({ company, form }) && Boolean(form.commercialId);
  const followed = company?.followed ?? null;

  const submit = () => {
    if (!valid || busy) return;
    setBusy(true);
    createProspect(buildProspectPayload({ company, form })).then(
      (result) => {
        // The api does not keep the company's address yet: the demo does.
        const email = form.email.trim();
        if (email && result?.company?.id) call(crmApi.setCompanyEmail, { companyId: result.company.id, email }).catch(() => {});
        toast(text('added'));
        onAdded?.(result);
        onClose();
      },
      (failure) => {
        setBusy(false);
        if (isCanceledRequest(failure)) return;
        toast(
          text('failed', {
            error: prospectionErrorText(prospectionText, failure),
          }),
          'error',
        );
      },
    );
  };

  const renderForm = () => (
    <>
      {company.manual ? (
        <TextField
          id="prospect-name"
          label={text('company.nameLabel')}
          icon={Building2}
          value={form.name}
          onChange={(event) => update('name', event.target.value)}
          onBlur={() => setNameLeft(true)}
          error={
            nameLeft && !form.name.trim()
              ? text('company.nameRequired')
              : undefined
          }
        />
      ) : (
        <CompanyCard company={company} onChange={change} />
      )}
      <TextField
        id="prospect-address"
        label={text('address.label')}
        icon={MapPin}
        value={form.address}
        helper={company.manual ? undefined : text('address.hint')}
        onChange={(event) => update('address', event.target.value)}
      />
      <PhoneField
        id="prospect-phone"
        label={text('phone.label')}
        placeholder={text('phone.placeholder')}
        value={form.phone}
        required
        onChange={(value) => update('phone', value)}
      />
      <TextField
        id="prospect-email"
        type="email"
        label={text('email.label')}
        icon={Mail}
        placeholder={text('email.placeholder')}
        value={form.email}
        onChange={(event) => update('email', event.target.value)}
        error={form.email.trim() && !isMail(form.email) ? text('email.invalid') : undefined}
      />
      <ProspectContacts
        contacts={form.contacts}
        onChange={(contacts) => update('contacts', contacts)}
      />
      <RichSelect
        id="prospect-category"
        label={text('category')}
        value={form.pennylaneCategoryId}
        options={[
          { value: '', label: text('noCategory') },
          ...categories.map((category) => ({
            value: category.id,
            label: category.label,
          })),
        ]}
        onChange={(value) => update('pennylaneCategoryId', value)}
      />
      <div className="flex flex-col gap-3">
        <p className="text-sm font-semibold text-ink">{text('source.label')}</p>
        <div className="flex flex-wrap gap-2">
          {PROSPECT_SOURCES.map((code) => (
            <Chip
              key={code}
              selected={form.source === code}
              onClick={() => update('source', code)}
            >
              {prospectionText(`sources.${code}`)}
            </Chip>
          ))}
        </div>
      </div>
      <RichSelect
        id="prospect-commercial"
        label={text('commercial.label')}
        placeholder={text('commercial.placeholder')}
        hint={text(commercial ? 'commercial.hintSelf' : 'commercial.hint')}
        value={form.commercialId}
        options={commercials.map((person) => ({
          value: person.id,
          label:
            person.id === userData?._id
              ? text('commercial.me', { name: `${person.firstname} ${person.lastname}`.trim() })
              : `${person.firstname} ${person.lastname}`.trim(),
        }))}
        onChange={(value) => update('commercialId', value)}
      />
    </>
  );

  const renderBody = () => {
    if (!company) {
      return <CompanyDirectorySearch onPick={pick} autoFocus />;
    }
    if (followed) {
      return (
        <FollowedCompany
          company={company}
          currentUserId={userData?._id}
          onChange={change}
        />
      );
    }
    return renderForm();
  };

  const footer = (
    <CrmScope className="flex flex-1 items-center justify-end gap-3">
      <Button variant="secondary" onClick={onClose}>
        {text('cancel')}
      </Button>
      {followed ? (
        <Button onClick={() => navigate(`/company/${followed.companyId}`)}>
          {text('followed.open')}
        </Button>
      ) : (
        <Button disabled={!valid || busy} onClick={submit}>
          {busy ? text('submitting') : text('submit')}
        </Button>
      )}
    </CrmScope>
  );
  const body = <CrmScope className="flex flex-col gap-6">{renderBody()}</CrmScope>;
  if (frame) return frame({ body, footer });
  return (
    <Drawer
      open
      onClose={onClose}
      title={text('title')}
      description={<CrmScope>{text('subtitle')}</CrmScope>}
      footer={footer}
    >
      {body}
    </Drawer>
  );
};

const AddProspectDrawer = ({ open, onClose, onAdded }) =>
  open ? <AddProspectForm onClose={onClose} onAdded={onAdded} /> : null;

export default AddProspectDrawer;
